import React from 'react'
import { RealEstateCategory, RealEstateFeatures, SegmentationType, TransactionType } from '../../../../../@types/types'
import { Flow, FlowName, FlowScreenElement } from '../../../../../@types/flow-types'
import { AGENT_INVESTMENT_FLOW_PATH } from '../../../../../services/routes/routes'
import {
  SCREEN_ADDRESS,
  SCREEN_API_SEARCH_DATA,
  SCREEN_BASIC_INFO,
  SCREEN_CONNECTIONS,
  SCREEN_CONSTRUCTION,
  SCREEN_CONTACT_INFO,
  SCREEN_DESCRIPTION,
  SCREEN_MULTIMEDIA,
  SCREEN_OBJECT_NUMBERS,
  SCREEN_OTHER_FACILITIES,
  SCREENS,
} from '../../../../../services/domain/wizardScreens'
import ContactInfo from '../../../../../components/pages/insertionPage/screens/contactInfo/ContactInfo'
import Address from '../../../../../components/pages/insertionPage/screens/address/Address'
import Construction from '../../../../../components/pages/insertionPage/screens/construction/Construction'
import Description from '../../../../../components/pages/insertionPage/screens/description/Description'
import Multimedia from '../../../../../components/pages/insertionPage/screens/multimediaNew/Multimedia'

import { MultimediaUploaderType } from '../../../../../@types/multimedia-types'
import Connections from '../../../../../components/pages/insertionPage/screens/connections/Connections'
import BasicInfo from '../../../../../components/pages/insertionPage/screens/basicInfo/BasicInfo'
import {
  additionalCostsField,
  buildingType,
  courtageGroup,
  livingSpaceField,
  mainFloorSpaceField,
  plotAreaField,
  priceField,
  purchasePriceOnRequestToggle,
  totalFloorSpaceField,
} from '../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoFieldsWrapper'
import { MainFloorType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/mainFloorSpaceField/MainFloorSpaceField'
import { PriceFieldType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/priceField/PriceField'
import { BuildingTypeOption } from '../../../../../@types/dropdown-types'
import OtherFacilities from '../../../../../components/pages/insertionPage/screens/otherFacilities/OtherFacilities'
import {
  additionalAreaField,
  estateFeatures,
  industrialAreaField,
  interiorQuality,
  parkingSpace,
  priceMultiplier,
  pricePerSQM,
  rentalIncomeActual,
  rentalIncomeTarget,
  serviceCharge,
  totalFloorsField,
} from '../../../../../components/pages/insertionPage/screens/otherFacilities/wrapper/otherFacilitiesFieldsWrapper'
import { getDefaultClasses } from '../../../../../utils/style-utils'
import { priceToApiSell } from '../../../../../api/realEstate/converters/toApi/estate/price/priceToApi'
import { courtageInformationToApi } from '../../../../../api/realEstate/converters/toApi/estate/courtageInformation/courtageInformationToApi'
import { ObjectNumbersScreen } from '../../../../../components/pages/insertionPage/screens/objectNumber/ObjectNumbersScreen'
import ApiSearchData from '../../../../../components/pages/insertionPage/screens/apiSearchData/ApiSearchData'
import { apiSearchDataToApi } from '../../../../../api/realEstate/converters/toApi/estate/apiSearchData/apiSearchDataToApi'
import { agentInvestmentSellSchema } from './agentInvestmentSellSchema'

const agentInvestmentSellFlowScreens: FlowScreenElement[] = [
  {
    screen: SCREENS[SCREEN_ADDRESS],
    element: (props) => <Address {...props} />,
  },
  {
    screen: SCREENS[SCREEN_OBJECT_NUMBERS],
    element: (props) => <ObjectNumbersScreen {...props} />,
  },
  {
    screen: SCREENS[SCREEN_BASIC_INFO],
    element: (props) => (
      <BasicInfo
        {...props}
        properties={[
          buildingType([
            BuildingTypeOption.SINGLE_FAMILY_HOUSE,
            BuildingTypeOption.MULTI_FAMILY_HOUSE,
            BuildingTypeOption.FREEHOLD_FLAT,
            BuildingTypeOption.SHOPPING_CENTRE,
            BuildingTypeOption.RESTAURANT,
            BuildingTypeOption.HOTEL,
            BuildingTypeOption.LEISURE_FACILITY,
            BuildingTypeOption.COMMERCIAL_UNIT,
            BuildingTypeOption.OFFICE_BUILDING,
            BuildingTypeOption.COMMERCIAL_BUILDING,
            BuildingTypeOption.COMMERCIAL_PROPERTY,
            BuildingTypeOption.HALL_STORAGE,
            BuildingTypeOption.INDUSTRIAL_PROPERTY,
            BuildingTypeOption.SHOP_SALES_FLOOR,
            BuildingTypeOption.SERVICE_CENTRE,
            BuildingTypeOption.SUPERMARKET,
            BuildingTypeOption.LIVING_BUSINESS_HOUSE,
            BuildingTypeOption.HOUSING_ESTATE,
            BuildingTypeOption.OTHER,
          ]),
          priceField(PriceFieldType.BUY_PRICE, 'left'),
          mainFloorSpaceField(MainFloorType.RENTAL_AREA, 'right'),
          purchasePriceOnRequestToggle(),
          courtageGroup(),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_OTHER_FACILITIES],
    element: (props) => (
      <OtherFacilities
        {...props}
        screenTooltipKey='tooltip.screens.otherFacilities.investmentSell'
        properties={[
          estateFeatures([RealEstateFeatures.LISTED, RealEstateFeatures.ELEVATOR]),
          interiorQuality(),
          pricePerSQM(),
          priceMultiplier(),
          plotAreaField('left', undefined),
          totalFloorSpaceField('right'),
          livingSpaceField('full'),
          industrialAreaField(),
          additionalAreaField(),
          serviceCharge(),
          additionalCostsField('full', 'unallocatedAdditionalCosts'),
          rentalIncomeActual(),
          rentalIncomeTarget(),
          totalFloorsField(getDefaultClasses('full')),
          parkingSpace({ showParkingSpaceType: false, parkingSpaceRentTypeLabel: 'RentPerItem' }),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_DESCRIPTION],
    element: (props) => <Description {...props} hasFurnishingNote={true} hasLocationNote={true} hasOtherNote={true} />,
  },
  {
    screen: SCREENS[SCREEN_MULTIMEDIA],
    element: (props) => (
      <Multimedia
        {...props}
        doCloseMultimediaStatusMessage={props.doCloseMultimediaStatusMessage!}
        allowedTypesToDisplay={[
          MultimediaUploaderType.PICTURE,
          MultimediaUploaderType.FLOOR_PLAN,
          MultimediaUploaderType.DOCUMENT,
          MultimediaUploaderType.VIDEO,
        ]}
        allowMultimediaLinks
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_CONNECTIONS],
    element: (props) => <Connections {...props} />,
  },
  {
    screen: SCREENS[SCREEN_CONSTRUCTION],
    element: (props) => <Construction {...props} hasLastModernizationField={true} />,
  },
  {
    screen: SCREENS[SCREEN_API_SEARCH_DATA],
    element: (props) => <ApiSearchData {...props} />,
  },
  {
    screen: SCREENS[SCREEN_CONTACT_INFO],
    element: (props) => <ContactInfo {...props} hasContactManager />,
  },
]

export const agentInvestmentSellFlow: Flow = {
  flowInformation: {
    owner: 'wizard.navigation.header.agent',
    estateType: 'wizard.navigation.header.investment',
    offerType: 'wizard.navigation.header.sells',
    path: AGENT_INVESTMENT_FLOW_PATH,
    name: FlowName.AGENT_INVESTMENT_SELL,
    validationSchema: agentInvestmentSellSchema,
    priceToApiMapper: priceToApiSell,
    courtageToApiMapper: courtageInformationToApi,
    apiSearchDataToApiMapper: apiSearchDataToApi,
    metaData: {
      category: RealEstateCategory.INVESTMENT,
      segmentation: SegmentationType.AGENT,
      transaction: TransactionType.SELL,
    },
  },
  defaultStateValues: { hideStreetAndStreetNumber: false },
  flowScreens: agentInvestmentSellFlowScreens,
}
